'use client';

import { useEffect, useRef, useState } from 'react';
import * as Blockly from 'blockly/core';
import {
    Activity,
    Bluetooth,
    Calculator,
    Cpu,
    File,
    FunctionSquare,
    GitPullRequest,
    Globe,
    MapPin,
    MonitorPlay,
    Projector,
    Siren,
    ToggleLeft,
    Variable,
    Wifi,
} from 'lucide-react';
import { useBlockFunctionStore } from '@/stores/useBlockFunctionStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { useBlockModalVariableStore } from '@/stores/useBlockModalVariableStore';
import { useBoardEditorConfigStore } from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { BOARD } from '@/(routers)/boards/editor/block/definitions/boards';
import { CATEGORIES, CATEGORY_GROUPS, CategoryId } from '@/(routers)/boards/editor/block/definitions/categories';
import { flyoutXmlFor } from '@/(routers)/boards/editor/block/definitions/flyout';

interface Props {
    workspace: Blockly.WorkspaceSvg | null;
}

type FlyoutXmlLike = string | Element | DocumentFragment | Element[];

const CATEGORY_ICONS: Record<CategoryId, React.ElementType> = {
    schoomy: Cpu,
    control: GitPullRequest,
    serial: MonitorPlay,
    action: Siren,
    sensing: Activity,
    gps: MapPin,
    file: File,
    switch: ToggleLeft,
    display: Projector,
    operator: Calculator,
    func: FunctionSquare,
    variable: Variable,
    bluetooth: Bluetooth,
    network: Wifi,
    api: Globe,
};

const FLYOUT_DURATION = '180ms';
const FLYOUT_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

const darkenColorCache = new Map<string, string>();
function darkenColor(hex: string): string {
    const cached = darkenColorCache.get(hex);
    if (cached) return cached;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const result = `rgb(${Math.round(r * 0.8)}, ${Math.round(g * 0.8)}, ${Math.round(b * 0.8)})`;
    darkenColorCache.set(hex, result);
    return result;
}

const STRIPE_W = 2;
let flyoutAccentGroup: SVGGElement | null = null;

function refreshFlyoutAccentSize(w: number, h: number): void {
    if (!flyoutAccentGroup) return;
    const stripe = flyoutAccentGroup.querySelector('rect') as SVGRectElement | null;
    if (!stripe) return;
    stripe.setAttribute('x', String(w - STRIPE_W));
    stripe.setAttribute('height', String(h));
}

function patchFlyoutAccent(color: string): void {
    if (typeof document === 'undefined') return;
    const svg = document.querySelector('svg.blocklyFlyout.blocklyToolboxFlyout') as SVGSVGElement | null;
    if (!svg) return;
    const w = Number(svg.getAttribute('width'));
    const h = Number(svg.getAttribute('height'));
    if (!w || !h) return;

    flyoutAccentGroup?.remove();
    flyoutAccentGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGElement;
    svg.appendChild(flyoutAccentGroup);

    const stripe = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    stripe.setAttribute('x', String(w - STRIPE_W));
    stripe.setAttribute('y', '0');
    stripe.setAttribute('width', String(STRIPE_W));
    stripe.setAttribute('height', String(h));
    stripe.style.fill = color;
    stripe.style.opacity = '1';
    flyoutAccentGroup.appendChild(stripe);

    requestAnimationFrame(() => {
        stripe.style.transition = 'opacity 0.8s ease';
        stripe.style.opacity = '0.35';
    });
}

function clearFlyoutAccent(): void {
    flyoutAccentGroup?.remove();
    flyoutAccentGroup = null;
}

const registeredCallbackKeys = new Set<string>();

function getFlyout(ws: Blockly.WorkspaceSvg): any {
    return (ws as any).getFlyout?.() ?? null;
}

function getFlyoutSvgGroup(flyout: any): SVGGElement | null {
    return (flyout?.svgGroup_ ?? flyout?.svgGroup ?? null) as SVGGElement | null;
}

function initFlyoutTransition(ws: Blockly.WorkspaceSvg): void {
    const flyout = getFlyout(ws);
    if (!flyout) return;
    const group = getFlyoutSvgGroup(flyout);
    if (!group) return;
    group.style.transition = `transform ${FLYOUT_DURATION} ${FLYOUT_EASING}, opacity ${FLYOUT_DURATION} ${FLYOUT_EASING}`;
    group.style.willChange = 'transform, opacity';
}

function setFlyoutVisible(ws: Blockly.WorkspaceSvg, visible: boolean): void {
    const flyout = getFlyout(ws);
    if (!flyout) return;
    const group = getFlyoutSvgGroup(flyout);

    if (visible) {
        flyout.setVisible?.(true);
        if (group) {
            group.style.transform = 'translateX(-16px)';
            group.style.opacity = '0';
            group.style.pointerEvents = 'none';
            requestAnimationFrame(() => {
                group.style.transform = 'translateX(0)';
                group.style.opacity = '1';
                group.style.pointerEvents = 'auto';
            });
        }
    } else {
        if (group) {
            group.style.transform = 'translateX(-16px)';
            group.style.opacity = '0';
            group.style.pointerEvents = 'none';
        }
        setTimeout(() => flyout.hide?.(), 220);
    }
}

export function forceFlyoutBackgroundHardRect(ws: Blockly.WorkspaceSvg): void {
    if (typeof document === 'undefined') return;
    const svg = document.querySelector('svg.blocklyFlyout.blocklyToolboxFlyout') as SVGSVGElement | null;
    const path = svg?.querySelector('path.blocklyFlyoutBackground') as SVGPathElement | null;
    if (!svg || !path) return;
    const w = Number(svg.getAttribute('width'));
    const h = Number(svg.getAttribute('height'));
    if (!w || !h) return;
    path.setAttribute('d', `M 0,0 H ${w} V ${h} H 0 Z`);
    refreshFlyoutAccentSize(w, h);
}

function toFlyoutContents(xml: FlyoutXmlLike): Element[] {
    if (Array.isArray(xml)) return xml;
    if (typeof xml === 'string') {
        let dom: any;
        try {
            dom = Blockly.utils.xml.textToDom(xml);
        } catch {
            dom = (Blockly.Xml as any).textToDom(xml);
        }
        if (dom?.children) return Array.from(dom.children) as Element[];
        if (dom?.nodeType === 1) return [dom as Element];
        return [];
    }
    const anyXml = xml as any;
    if (anyXml?.children) return Array.from(anyXml.children) as Element[];
    if (anyXml?.nodeType === 1) return [xml as Element];
    return [];
}

export function applyFlyoutLayout(
    ws: Blockly.WorkspaceSvg,
    opts?: { scale?: number; margin?: number; gapY?: number },
): void {
    const flyout = getFlyout(ws);
    if (!flyout) return;
    const fws = flyout.getWorkspace?.();
    if (!fws) return;

    const scale = opts?.scale ?? ws.getScale();
    const margin = opts?.margin ?? 23;
    const gapY = opts?.gapY ?? 30;

    if (typeof fws.setScale === 'function') {
        fws.setScale(scale);
    } else {
        (fws as any).scale = scale;
    }

    if ('MARGIN' in flyout) (flyout as any).MARGIN = margin;
    if ('GAP_Y' in flyout) (flyout as any).GAP_Y = gapY;

    flyout.reflow?.();

    try {
        (flyout as any).scrollbar_?.resize?.();
    } catch {}
}

function patchFlyoutLabels(ws: Blockly.WorkspaceSvg, color: string): void {
    const flyout = getFlyout(ws);
    const svgGroup = getFlyoutSvgGroup(flyout);
    if (!svgGroup) return;

    // spacerブロックを非表示
    const flyoutWs = flyout.getWorkspace?.();
    if (flyoutWs) {
        (flyoutWs.getAllBlocks(false) as Blockly.Block[]).forEach((b) => {
            if (b.type === 'spacer') {
                const svg = (b as any).svgGroup_ as SVGElement | null;
                if (svg) {
                    svg.setAttribute('display', 'none');
                    svg.style.visibility = 'hidden';
                    svg.style.pointerEvents = 'none';
                    svg.querySelectorAll('*').forEach((el) => {
                        (el as SVGElement).style.pointerEvents = 'none';
                    });
                }
                (b as any).pathObject?.svgPath?.setAttribute('display', 'none');
                const id = (b as any).id;
                svgGroup.querySelectorAll(`[data-id="${id}"]`).forEach((el) => {
                    (el as SVGElement).style.pointerEvents = 'none';
                });
            }
        });
    }

    const svgWidth = parseFloat(svgGroup.getAttribute('width') ?? '300');
    const darkColor = darkenColor(color);

    const buttons = svgGroup.querySelectorAll('g.blocklyFlyoutButton');
    buttons.forEach((g) => {
        const textEl = g.querySelector('text');
        const labelText = textEl?.textContent ?? '';
        if (!labelText) return;

        if (g.querySelector('foreignObject')) return;

        const transform = g.getAttribute('transform') ?? '';
        const match = transform.match(/translate\(([^,]+),/);
        const offsetX = match ? parseFloat(match[1]) : 31;

        for (const el of Array.from(g.children)) {
            const tag = el.tagName.toLowerCase();
            if (tag === 'rect' || tag === 'text') {
                (el as SVGElement).style.display = 'none';
            }
        }

        const isActionButton = labelText === '関数の管理' || labelText === '変数の管理';

        const fo = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
        fo.setAttribute('width', String(svgWidth - offsetX));
        fo.setAttribute('height', '52');
        fo.setAttribute('x', '0');
        fo.setAttribute('y', '-12');

        const div = document.createElement('div');
        div.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
        div.style.cssText = [
            'display:flex',
            'justify-content:flex-start',
            'align-items:center',
            'width:100%',
            'height:52px',
            'box-sizing:border-box',
        ].join(';');

        const badge = document.createElement('div');

        if (isActionButton) {
            badge.style.cssText = [
                'display:inline-flex',
                'align-items:center',
                'justify-content:center',
                'gap:8px',
                'width:100%',
                'padding:10px 24px',
                'box-sizing:border-box',
                'background:transparent',
                `border:2px solid ${darkColor}`,
                'border-radius:9999px',
                "font-family:'Corporate Logo',sans-serif",
                'font-weight:600',
                'font-size:18px',
                `color:${darkColor}`,
                'white-space:nowrap',
                'user-select:none',
                'letter-spacing:0.06em',
                'cursor:pointer',
                `box-shadow:0 2px 8px ${color}60`,
            ].join(';');

            const icon = document.createElement('span');
            icon.style.cssText = 'display:inline-flex;align-items:center;line-height:1;';
            icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${darkColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;
            badge.appendChild(icon);

            badge.addEventListener('mouseenter', () => {
                badge.style.background = `${color}10`;
            });
            badge.addEventListener('mouseleave', () => {
                badge.style.background = 'transparent';
            });
        } else {
            badge.style.cssText = [
                'display:inline-flex',
                'align-items:center',
                'justify-content:center',
                'gap:6px',
                'width:60%',
                'padding:7px 0',
                `background:${color}50`,
                'border-radius:9999px',
                "font-family:'Corporate Logo',sans-serif",
                'font-weight:500',
                'font-size:16px',
                `color:${darkColor}`,
                'white-space:nowrap',
                'user-select:none',
                'letter-spacing:0.06em',
            ].join(';');
        }

        const text = document.createElement('span');
        text.textContent = labelText;

        badge.appendChild(text);
        div.appendChild(badge);
        fo.appendChild(div);
        g.appendChild(fo);
    });
}

function showFlyout(ws: Blockly.WorkspaceSvg, xml: FlyoutXmlLike): void {
    const flyout = getFlyout(ws);
    if (!flyout) return;

    applyFlyoutLayout(ws);

    const contents = toFlyoutContents(xml);
    if (typeof flyout.show === 'function') {
        flyout.show(contents);
    } else {
        (ws as any).updateToolbox?.(xml);
    }

    flyout.setVisible?.(true);
    forceFlyoutBackgroundHardRect(ws);

    requestAnimationFrame(() => {
        applyFlyoutLayout(ws);
        forceFlyoutBackgroundHardRect(ws);
        initFlyoutTransition(ws);

        requestAnimationFrame(() => {
            applyFlyoutLayout(ws);
            forceFlyoutBackgroundHardRect(ws);
        });
    });
}

function registerFuncLabelCallbacks(workspace: Blockly.WorkspaceSvg): void {
    const fns = useBlockFunctionStore.getState().functions;
    fns.forEach((fn) => {
        const key = `__flyout_label__func__fn__${fn.id}`;
        if (!registeredCallbackKeys.has(key)) {
            try {
                workspace.registerButtonCallback(key, () => {});
                registeredCallbackKeys.add(key);
            } catch {}
        }
    });
}

export default function BoardBlockEditorCategory({ workspace }: Props) {
    const { isDark } = useBoardEditorConfigStore();
    const board = useBoardEditorStore((s) => s.board);
    const getCategoryColor = (id: CategoryId): string => (id === 'schoomy' ? BOARD[board].color : CATEGORIES[id].color);
    const [selectedId, setSelectedId] = useState<CategoryId | null>(null);
    const [open, setOpen] = useState(false);
    const lastFlyoutIdRef = useRef<CategoryId | null>(null);

    const functions = useBlockFunctionStore((s) => s.functions);
    const modalIsOpen = useBlockModalStore((s) => s.isOpen);

    // 関数ストアが変わったらflyoutを再描画
    useEffect(() => {
        if (!workspace || lastFlyoutIdRef.current !== 'func' || !open) return;
        registerFuncLabelCallbacks(workspace);
        showFlyout(workspace, flyoutXmlFor('func', workspace) as FlyoutXmlLike);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                patchFlyoutLabels(workspace, CATEGORIES.func.color);
            });
        });
    }, [functions, workspace]);

    // モーダルが閉じたら変数flyoutを再描画
    useEffect(() => {
        if (!workspace || modalIsOpen) return;
        if (lastFlyoutIdRef.current === 'variable' && open) {
            showFlyout(workspace, flyoutXmlFor('variable', workspace) as FlyoutXmlLike);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    patchFlyoutLabels(workspace, CATEGORIES.variable.color);
                });
            });
        }
    }, [modalIsOpen, workspace]);

    // schoomyカテゴリのflyoutを開いたままボード種別を切り替えると、色源(BOARD[board].color)は
    // 変わってもflyout自体はopenFlyout()実行時の色のまま残る。上2つの再描画effectと同じ考え方で、
    // board変更時にもflyoutが開いていれば色を再適用する。
    useEffect(() => {
        if (!workspace || lastFlyoutIdRef.current !== 'schoomy' || !open) return;
        const color = getCategoryColor('schoomy');
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                patchFlyoutLabels(workspace, color);
                patchFlyoutAccent(color);
            });
        });
    }, [board, workspace]);

    useEffect(() => {
        if (!workspace) return;
        setSelectedId(null);
        setOpen(false);
        lastFlyoutIdRef.current = null;
        registeredCallbackKeys.clear();
        initFlyoutTransition(workspace);
        setFlyoutVisible(workspace, false);
    }, [workspace]);

    useEffect(() => {
        if (!workspace) return;
        const handler = () => {
            setOpen(false);
            setFlyoutVisible(workspace, false);
        };
        window.addEventListener('blockly-flyout-close', handler);
        return () => window.removeEventListener('blockly-flyout-close', handler);
    }, [workspace]);

    const openFlyout = (id: CategoryId): void => {
        if (!workspace) return;

        if (id === 'variable') {
            useBlockModalVariableStore.getState().setWorkspace(workspace);
            const createKey = 'CREATE_VARIABLE';
            if (!registeredCallbackKeys.has(createKey)) {
                try {
                    workspace.registerButtonCallback(createKey, () => {
                        useBlockModalVariableStore.getState().setWorkspace(workspace);
                        useBlockModalVariableStore.getState().setFilterType(null);
                        useBlockModalStore.getState().openModal('variable');
                    });
                    registeredCallbackKeys.add(createKey);
                } catch {}
            }
        }

        if (id === 'func') {
            registerFuncLabelCallbacks(workspace);
            const createKey = 'CREATE_FUNCTION';
            if (!registeredCallbackKeys.has(createKey)) {
                try {
                    workspace.registerButtonCallback(createKey, () => {
                        useBlockModalStore.getState().openModal('func');
                    });
                    registeredCallbackKeys.add(createKey);
                } catch {}
            }
        }

        lastFlyoutIdRef.current = id;
        const color = getCategoryColor(id);

        CATEGORIES[id].blocks
            .filter((e) => e.visible && e.type.startsWith('__label__'))
            .forEach((e) => {
                const text = e.type.replace('__label__', '');
                const key = `__flyout_label__${id}__${text}`;
                if (!registeredCallbackKeys.has(key)) {
                    try {
                        workspace.registerButtonCallback(key, () => {});
                        registeredCallbackKeys.add(key);
                    } catch {}
                }
            });

        if (id === 'variable') {
            ['数値', '文字列'].forEach((text) => {
                const key = `__flyout_label__variable__${text}`;
                if (!registeredCallbackKeys.has(key)) {
                    try {
                        workspace.registerButtonCallback(key, () => {});
                        registeredCallbackKeys.add(key);
                    } catch {}
                }
            });
        }

        showFlyout(workspace, flyoutXmlFor(id, workspace) as FlyoutXmlLike);
        setFlyoutVisible(workspace, true);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                patchFlyoutLabels(workspace, color);
                patchFlyoutAccent(color);
            });
        });
    };

    const closeFlyout = (): void => {
        if (!workspace) return;
        setFlyoutVisible(workspace, false);
        clearFlyoutAccent();
    };

    const handleCategoryClick = (id: CategoryId): void => {
        if (!workspace) return;

        if (selectedId === id) {
            const nextOpen = !open;
            setOpen(nextOpen);
            if (nextOpen) openFlyout(id);
            else closeFlyout();
            return;
        }

        setSelectedId(id);
        setOpen(true);
        openFlyout(id);
    };

    return (
        <div
            suppressHydrationWarning
            className={`h-full flex flex-col w-[48px] md:w-[180px] border-r ${
                isDark ? 'bg-[#111111] border-gray-700' : 'bg-[#ffffff] border-gray-200'
            }`}
        >
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center py-2 gap-0.5 px-1 md:px-2 category-scroll">
                {CATEGORY_GROUPS.map(({ label, ids }) => {
                    const visibleIds = ids.filter(
                        (id) => CATEGORIES[id].blocks.some((e) => e.visible) || id === 'variable' || id === 'func',
                    );
                    if (visibleIds.length === 0) return null;
                    return (
                        <div key={label} className="w-full">
                            {label && (
                                <div className="hidden md:flex px-1 pt-3 pb-0.5 items-center gap-2">
                                    <span
                                        className={`font-montserrat text-[9px] font-bold tracking-wider px-2 py-0.5 rounded-full ${
                                            isDark ? 'text-gray-500' : 'text-gray-400'
                                        }`}
                                    >
                                        {label}
                                    </span>
                                </div>
                            )}
                            {visibleIds.map((id) => {
                                const Icon = CATEGORY_ICONS[id];
                                const { name } = CATEGORIES[id];
                                const color = getCategoryColor(id);
                                const isSelected = selectedId === id;
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => handleCategoryClick(id)}
                                        className={`w-full flex-shrink-0 flex items-center justify-center md:justify-start gap-3 py-1.5 px-1 md:px-3 rounded-lg transition-colors duration-150 cursor-pointer ${
                                            isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'
                                        }`}
                                        style={isSelected ? { backgroundColor: color } : {}}
                                        suppressHydrationWarning
                                    >
                                        <div
                                            className="w-8 h-8 md:w-9 md:h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : color }}
                                            suppressHydrationWarning
                                        >
                                            <Icon size={20} strokeWidth={2} color="white" />
                                        </div>
                                        <div className="hidden md:flex flex-col items-start">
                                            <span
                                                className="font-corp text-sm font-medium"
                                                style={{ color: isSelected ? 'white' : isDark ? '#e5e7eb' : 'black' }}
                                            >
                                                {name}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
